// Pure data-shaping for the Roadmap mind map — PJ's 2026-09-09 ask, rebuilt on
// mind-elixir (MIT) after the owner's review of the hand-rolled Cytoscape map
// ("kind of ugly — use a good open-source mind map package, or emulate the good
// ones"). mind-elixir draws the classic XMind-style two-sided map: root pill in
// the middle, thick coloured main branches, topics sitting on curved lines,
// collapsible sub-topics. Kept out of the component so the tree, the tags and
// the arrows are unit-testable without a DOM.
//
// Shape: root = the roadmap · main branches = task forces (coloured by team,
// balanced left/right by size) · topics = initiatives (a cross-team item sits
// under EACH of its teams, tagged) · sub-topics = live sub-actions. Arrows
// between topics carry depends_on (blocker → blocked, red dashed) and, on
// request, related_to (grey dotted, both ways).

import type { MindElixirData, NodeObj, TagObj } from 'mind-elixir';

// mind-elixir 5.x does not re-export its Arrow type; derive it from the document.
export type MindMapArrow = NonNullable<MindElixirData['arrows']>[number];
import type { InitiativeState, ScheduleHealth } from '../../services/roadmapReports';
import {
  ACTION_STATUS_LABELS,
  foldTeam,
  HEALTH_LABELS,
  liveActions,
  mandateAreaTitle,
  NO_TEAM,
  NO_TEAM_COLOR,
  OTHER_AREA,
  OTHER_AREA_TITLE,
  UNFILED_AREA_TITLE,
  type MandateLookup,
  type TeamColors,
  groupTeamsOf,
  relatedTeamsOf,
} from './roadmapViewUtils';

/** What a node stands for — read back on click to drill into its initiative. */
export type MindMapMeta =
  | { kind: 'root' }
  | { kind: 'team'; team: string }
  // v10: a mandate area of a team (area null = the team's unfiled work).
  | { kind: 'mandate'; team: string; area: string | null }
  | { kind: 'initiative'; key: string }
  | { kind: 'action'; key: string; actionKey: string };

export type MindMapNode = NodeObj<MindMapMeta>;
export type MindMapData = Omit<MindElixirData, 'nodeData'> & { nodeData: MindMapNode };

export interface MindMapOptions {
  /** Draw the related_to links as grey two-way arrows (off by default — noisy). */
  showRelated: boolean;
  /** Start with sub-actions open under their initiative. */
  expandActions: boolean;
}

// Sub-actions start folded: with ~100 of them open the default view is
// unreadable until zoomed (owner review 4jsvwe); one click opens them all.
export const DEFAULT_MIND_MAP_OPTIONS: MindMapOptions = { showRelated: false, expandActions: false };

// mind-elixir's direction constants (a main branch's side of the root).
export const LEFT = 0 as const;
export const RIGHT = 1 as const;

/** Chip colours for the schedule-health tag on an initiative topic. */
export const HEALTH_TAG_STYLE: Record<ScheduleHealth, { color: string; background: string }> = {
  late: { color: '#991b1b', background: '#fee2e2' },
  at_risk: { color: '#92400e', background: '#fef3c7' },
  stuck: { color: '#92400e', background: '#fef3c7' },
  no_target: { color: '#475569', background: '#e2e8f0' },
  on_track: { color: '#166534', background: '#dcfce7' },
  done: { color: '#166534', background: '#dcfce7' },
  parked: { color: '#475569', background: '#e0e7ff' },
  dropped: { color: '#64748b', background: '#f1f5f9' },
};

export const DEPENDS_ARROW_STYLE = { stroke: '#dc2626', strokeWidth: 2, strokeDasharray: '7 5' };
// How far an arrow between two topics on the SAME side bows outward, so it
// arcs past the column of topics instead of cutting straight through their text.
export const ARROW_BOW = 240;

type Side = typeof LEFT | typeof RIGHT;

/** Control-point offsets for an arrow: same-side arrows bow away from the root
 * (right side → bulge right, left side → bulge left); cross-side arrows keep
 * mind-elixir's default curve through the middle. */
export function arrowDeltas(fromSide: Side | undefined, toSide: Side | undefined): Pick<MindMapArrow, 'delta1' | 'delta2'> {
  if (fromSide === undefined || fromSide !== toSide) return {};
  const x = fromSide === RIGHT ? ARROW_BOW : -ARROW_BOW;
  return { delta1: { x, y: 0 }, delta2: { x, y: 0 } };
}
export const RELATED_ARROW_STYLE = { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '2 5' };

export const ROOT_ID = 'roadmap-root';
export const teamNodeId = (team: string): string => `team:${team.toLowerCase()}`;
export const mandateNodeId = (team: string, area: string | null): string => `area:${team.toLowerCase()}:${area ?? '_unfiled'}`;
export const initiativeNodeId = (team: string, key: string): string => `init:${team.toLowerCase()}:${key}`;
export const actionNodeId = (team: string, key: string, actionKey: string): string =>
  `act:${team.toLowerCase()}:${key}:${actionKey}`;

function healthTag(health: ScheduleHealth): TagObj {
  return { text: HEALTH_LABELS[health], style: HEALTH_TAG_STYLE[health] };
}

/** "Scott Alter, Hadi Tawil +2" — who holds it, without swamping the topic. */
export function peopleTag(people: string[]): TagObj | null {
  if (people.length === 0) return null;
  const shown = people.slice(0, 2).join(', ');
  const more = people.length - 2;
  return { text: more > 0 ? `${shown} +${more}` : shown, style: { color: '#334155', background: '#f1f5f9' } };
}

function actionNode(team: string, i: InitiativeState, a: InitiativeState['actions'][number]): MindMapNode {
  const tags: (string | TagObj)[] = [];
  if (a.status !== 'unknown') {
    tags.push({
      text: ACTION_STATUS_LABELS[a.status],
      style: a.status === 'done' ? HEALTH_TAG_STYLE.done : a.status === 'in_progress' ? { color: '#1d4ed8', background: '#dbeafe' } : HEALTH_TAG_STYLE.no_target,
    });
  }
  const who = peopleTag(a.people);
  if (who) tags.push(who);
  return {
    id: actionNodeId(team, i.initiative_key, a.key),
    topic: a.title,
    tags,
    style: a.status === 'done' ? { textDecoration: 'line-through', color: '#64748b' } : undefined,
    metadata: { kind: 'action', key: i.initiative_key, actionKey: a.key },
  };
}

function initiativeNode(team: string, i: InitiativeState, opts: MindMapOptions): MindMapNode {
  const tags: (string | TagObj)[] = [healthTag(i.health)];
  if (relatedTeamsOf(i).length > 0) tags.push({ text: 'cross-team', style: { color: '#3730a3', background: '#e0e7ff' } });
  const who = peopleTag(i.people);
  if (who) tags.push(who);
  const actions = liveActions(i.actions).map((a) => actionNode(team, i, a));
  return {
    id: initiativeNodeId(team, i.initiative_key),
    topic: i.title,
    tags,
    // Late work reads red at a glance, matching the timeline's late outline.
    style: i.health === 'late' ? { color: '#b91c1c', fontWeight: '600' } : undefined,
    children: actions.length > 0 ? actions : undefined,
    expanded: actions.length > 0 ? opts.expandActions : undefined,
    metadata: { kind: 'initiative', key: i.initiative_key },
  };
}

interface TeamBucket { name: string; color: string; items: InitiativeState[] }

/** Teams in the order they should be drawn: biggest first (they anchor the
 * sides), the "no team" bucket last. */
export function teamBuckets(initiatives: InitiativeState[], colors: TeamColors): TeamBucket[] {
  const buckets = new Map<string, TeamBucket>();
  for (const i of initiatives) {
    // v9: an owned initiative sits under its owner only; a related team's
    // branch does not claim it.
    const names = groupTeamsOf(i);
    for (const t of names) {
      const k = t.toLowerCase();
      const b = buckets.get(k) ?? { name: t, color: t === NO_TEAM ? NO_TEAM_COLOR : colors[t] ?? NO_TEAM_COLOR, items: [] };
      b.items.push(i);
      buckets.set(k, b);
    }
  }
  const all = [...buckets.values()];
  const real = all.filter((b) => b.name !== NO_TEAM).sort((a, b) => b.items.length - a.items.length || a.name.localeCompare(b.name));
  const none = all.filter((b) => b.name === NO_TEAM);
  return [...real, ...none];
}

/** Rows a bucket will occupy (initiatives + open sub-actions) — what balances the sides. */
function weight(b: TeamBucket, opts: MindMapOptions): number {
  return b.items.reduce((n, i) => n + 1 + (opts.expandActions ? liveActions(i.actions).length : 0), 0);
}

/**
 * Which side of the root each team branch goes on: greedy — each team joins the
 * lighter side, so a 17 / 9 / 3 split lands as 17 on one side, 9 + 3 on the
 * other, instead of mind-elixir's default alternation (17 + 3 vs 9).
 */
export function balanceSides(buckets: TeamBucket[], opts: MindMapOptions): Map<string, typeof LEFT | typeof RIGHT> {
  const side = new Map<string, typeof LEFT | typeof RIGHT>();
  let right = 0;
  let left = 0;
  for (const b of buckets) {
    const w = weight(b, opts);
    if (right <= left) {
      side.set(b.name, RIGHT);
      right += w;
    } else {
      side.set(b.name, LEFT);
      left += w;
    }
  }
  return side;
}

/**
 * The full mind-elixir document for the initiatives currently shown. Dropped
 * initiatives are history, not plan, and are left off. Arrows only join
 * initiatives that are both on the map (to their FIRST occurrence, so a
 * cross-team item is targeted once).
 */
/**
 * The branches under a team that has a mandate on record (v10, PJ 2026-10-12:
 * "whatever information is underneath this button when I push it is going
 * to have to do with housekeeping on the Alpha team"): one node per area in
 * the team's order, then the team's work outside its mandate, then what is
 * not filed yet. Empty areas are left off; the "not filed" node is muted.
 */
export function mandateBranches(
  team: string,
  items: InitiativeState[],
  lookup: MandateLookup,
  topic: (i: InitiativeState) => MindMapNode,
): MindMapNode[] | null {
  const areas = lookup[foldTeam(team)];
  if (!areas || areas.length === 0) return null;
  const known = new Set([...areas.map((a) => a.key), OTHER_AREA]);
  const by = new Map<string | null, InitiativeState[]>();
  for (const i of items) {
    const key = i.mandate_area && known.has(i.mandate_area) ? i.mandate_area : null;
    by.set(key, [...(by.get(key) ?? []), i]);
  }
  const order: Array<{ key: string | null; title: string; muted: boolean }> = [
    ...areas.map((a) => ({ key: a.key, title: a.title, muted: false })),
    { key: OTHER_AREA, title: OTHER_AREA_TITLE, muted: false },
    { key: null, title: UNFILED_AREA_TITLE, muted: true },
  ];
  return order
    .filter((o) => (by.get(o.key) ?? []).length > 0)
    .map((o) => {
      const members = by.get(o.key) ?? [];
      return {
        id: mandateNodeId(team, o.key),
        topic: o.title,
        tags: [`${members.length}`],
        style: o.muted ? { color: '#64748b', fontStyle: 'italic' } : { fontWeight: '600' },
        expanded: true,
        children: members.map(topic),
        metadata: { kind: 'mandate', team, area: o.key },
      };
    });
}

export function buildMindMapData(
  initiatives: InitiativeState[],
  colors: TeamColors,
  opts: MindMapOptions = DEFAULT_MIND_MAP_OPTIONS,
  mandates: MandateLookup = {},
): MindMapData {
  const shown = initiatives.filter((i) => i.status !== 'dropped');
  const buckets = teamBuckets(shown, colors);
  const sides = balanceSides(buckets, opts);
  const firstNode = new Map<string, string>();
  const sideOf = new Map<string, Side>();

  const mains: MindMapNode[] = buckets.map((b) => {
    const topic = (i: InitiativeState): MindMapNode => {
      if (!firstNode.has(i.initiative_key)) {
        firstNode.set(i.initiative_key, initiativeNodeId(b.name, i.initiative_key));
        sideOf.set(i.initiative_key, sides.get(b.name) ?? RIGHT);
      }
      const node = initiativeNode(b.name, i, opts);
      const area = mandateAreaTitle(i, mandates);
      return area ? { ...node, tags: [...(node.tags ?? []), { text: area, style: { color: '#1e3a8a', background: '#e0e7ff' } }] } : node;
    };
    const children = (b.name === NO_TEAM ? null : mandateBranches(b.name, b.items, mandates, topic)) ?? b.items.map(topic);
    return {
      id: teamNodeId(b.name),
      topic: b.name,
      tags: [`${b.items.length} initiative${b.items.length === 1 ? '' : 's'}`],
      branchColor: b.color,
      style: { background: b.color, color: '#ffffff', fontWeight: '700', border: `2px solid ${b.color}` },
      direction: sides.get(b.name),
      expanded: true,
      children,
      metadata: { kind: 'team', team: b.name },
    };
  });

  const active = shown.filter((i) => i.status !== 'done').length;
  const root: MindMapNode = {
    id: ROOT_ID,
    topic: 'Initiative Roadmap',
    tags: [`${active} active`, `${buckets.filter((b) => b.name !== NO_TEAM).length} task force${buckets.length === 1 ? '' : 's'}`],
    expanded: true,
    children: mains,
    metadata: { kind: 'root' },
  };

  const arrows: MindMapArrow[] = [];
  const related = new Set<string>();
  for (const i of shown) {
    const to = firstNode.get(i.initiative_key)!;
    for (const dep of i.depends_on) {
      const from = firstNode.get(dep);
      if (!from || dep === i.initiative_key) continue;
      arrows.push({
        id: `dep:${dep}:${i.initiative_key}`,
        label: '',
        from,
        to,
        style: DEPENDS_ARROW_STYLE,
        ...arrowDeltas(sideOf.get(dep), sideOf.get(i.initiative_key)),
      });
    }
    if (!opts.showRelated) continue;
    for (const rel of i.related_to) {
      const other = firstNode.get(rel);
      if (!other || rel === i.initiative_key) continue;
      const pair = [i.initiative_key, rel].sort().join('|');
      if (related.has(pair)) continue;
      related.add(pair);
      arrows.push({
        id: `rel:${pair}`,
        label: '',
        from: to,
        to: other,
        bidirectional: true,
        style: RELATED_ARROW_STYLE,
        ...arrowDeltas(sideOf.get(i.initiative_key), sideOf.get(rel)),
      });
    }
  }

  return { nodeData: root, arrows, direction: 2 /* MindElixir.SIDE */ };
}

/** The initiative a clicked node drills into, or null (root / team). */
export function initiativeKeyOfMeta(meta: MindMapMeta | undefined): string | null {
  return meta && (meta.kind === 'initiative' || meta.kind === 'action') ? meta.key : null;
}

/** The sub-action a click lands on, so the pop-up can scope to it (kanban 2a5883db). */
export function actionKeyOfMeta(meta: MindMapMeta | undefined): string | undefined {
  return meta && meta.kind === 'action' ? meta.actionKey : undefined;
}

/** How many live sub-actions the map could show — drives the toggle's label. */
export function countActions(initiatives: InitiativeState[]): number {
  return initiatives.filter((i) => i.status !== 'dropped').reduce((n, i) => n + liveActions(i.actions).length, 0);
}

/** How many related links the map could show — drives the toggle's label. */
export function countRelated(initiatives: InitiativeState[]): number {
  const shown = new Set(initiatives.filter((i) => i.status !== 'dropped').map((i) => i.initiative_key));
  const pairs = new Set<string>();
  for (const i of initiatives) {
    if (!shown.has(i.initiative_key)) continue;
    for (const r of i.related_to) if (shown.has(r) && r !== i.initiative_key) pairs.add([i.initiative_key, r].sort().join('|'));
  }
  return pairs.size;
}
